import { useSearch } from "@tanstack/react-router"
import { useCallback } from "react"
import type { Client, CombinedError } from "urql"

import type { EquivalentProductGroupSearchFilters } from "../stores/useEquivalentProductGroupSearchStore"
import { useEquivalentProductGroupSearchStore } from "../stores/useEquivalentProductGroupSearchStore"

import type {
  AssosiatedProductType,
  ComputedProductStatus,
  EquivalencyGroup,
  EquivalencyGroupSortField,
  EquivalencyType,
  SortingOrder,
} from "@/graphql/codegen/graphql"
import { queryClient } from "@/providers/GraphqlRouterProvider"

interface UseEquivalentProductGroupSearchListReturn {
  searchParams: {
    query?: string
    field?: EquivalencyGroupSortField
    order?: SortingOrder
    equivalentTypes?: EquivalencyType[]
    productStatuses?: ComputedProductStatus[]
    associationTypes?: AssosiatedProductType[]
  }
  data: EquivalencyGroup[]
  fetching: boolean
  error: CombinedError | Error | undefined
  executeSearch: (
    client: Client,
    filters: EquivalentProductGroupSearchFilters,
    skipFilterCheck?: boolean
  ) => Promise<void>
  refetchData: (skipCheck?: boolean) => Promise<void>
  clearStore: () => void
}

const useEquivalentProductGroupSearchList = (): UseEquivalentProductGroupSearchListReturn => {
  const currentSearch = useSearch({ from: "/app/admin/manage-products/_layout/equivalents" })
  const { query, field, order, equivalentTypes, productStatuses, associationTypes } = currentSearch

  const executeSearch = useEquivalentProductGroupSearchStore(({ executeSearch }) => executeSearch)
  const data = useEquivalentProductGroupSearchStore(({ data }) => data)
  const fetching = useEquivalentProductGroupSearchStore(({ fetching }) => fetching)
  const error = useEquivalentProductGroupSearchStore(({ error }) => error)
  const clearStore = useEquivalentProductGroupSearchStore(({ clearStore }) => clearStore)

  const refetchData = useCallback(
    async (skipCheck = true) => {
      return executeSearch(queryClient, currentSearch, skipCheck)
    },
    [currentSearch, executeSearch]
  )

  return {
    searchParams: {
      query,
      field,
      order,
      equivalentTypes,
      productStatuses,
      associationTypes,
    },
    data,
    fetching,
    error,
    executeSearch,
    refetchData,
    clearStore,
  }
}

export default useEquivalentProductGroupSearchList
